import Tag from "@/components/atoms/Tag";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { CalendarIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";
import Link from "next/link";
import React from "react";
import { Exercise } from "@/types/ContentTypes/Exercise";

export interface ExerciseListItemProps {
  exercise: Exercise;
}

interface TechStack {
  attributes: {
    name: string;
    color: string;
  };
}

const ExerciseListItem: React.FC<{ exercise: Exercise }> = ({ exercise }) => (
  <li>
    <Link
      href={`/ejercicios/${exercise.attributes.slug}`}
      className="block hover:bg-gray-50"
    >
      <div className="flex items-center px-4 py-4 sm:px-6">
        <div className="min-w-0 flex-1 sm:flex sm:items-center sm:justify-between">
          <div className="truncate">
            <div className="flex">
              <p className="truncate font-medium text-primary">
                {exercise.attributes.title}
              </p>
            </div>
            <div className="flex">
              <p className="truncate text-sm text-gray-500">
                {exercise.attributes.hook}
              </p>
            </div>
            <div className="mt-2 flex gap-2">
              {exercise.attributes.techStack?.data.map((tech: TechStack) => (
                <Tag
                  key={tech.attributes.name}
                  type={tech.attributes.name}
                  color={tech.attributes.color}
                />
              ))}
              <Tag
                key={exercise.attributes.level}
                type={exercise.attributes.level}
                color={
                  exercise.attributes.level === "Principiante"
                    ? "green"
                    : "yellow"
                }
              />
            </div>
            <div className="mt-2 flex items-center">
              <CalendarIcon
                className="mr-1.5 h-4 w-4 flex-shrink-0 text-gray-400"
                aria-hidden="true"
              />
              <p className="text-sm text-gray-400">
                <time dateTime={exercise.attributes.publishedAt}>
                  {DateTime.fromISO(exercise.attributes.publishedAt)
                    .setLocale("es")
                    .toFormat("dd LLLL yyyy")}
                </time>
              </p>
            </div>
          </div>
          <div className="mt-4 flex-shrink-0 sm:ml-5 sm:mt-0">
            <div className="flex -space-x-1 overflow-hidden">
              {exercise.attributes.users?.map(({ user }: any) => (
                <img
                  key={user.id}
                  className="inline-block h-6 w-6 rounded-full ring-2 ring-white"
                  src={
                    user.profilePhoto?.url
                      ? process.env.NEXT_PUBLIC_CMS_URL + user.profilePhoto?.url
                      : "https://images.unsplash.com/photo-1502685104226-ee32379fefbe?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
                  }
                  alt={user.username}
                />
              ))}
              <span className="pl-3 text-gray-500">
                {exercise.attributes.submissions > 3 && (
                  <span>
                    +{exercise.attributes.submissions - 3}{" "}
                    {exercise.attributes.submissions - 3 === 1
                      ? "persona"
                      : "personas"}
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>
        <div className="ml-5 flex-shrink-0">
          <ChevronRightIcon
            className="h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
        </div>
      </div>
    </Link>
  </li>
);

export default ExerciseListItem;
